import {Form, FormField, FormSubmitHandler, showNotification} from 'platform/components';
import {Grid} from 'platform/foundation';

import {isNil} from 'ramda';

import {
  GetTireCategoryApiResponse,
  useGetBranchListQuery,
  usePatchTireCategoryMutation,
  usePostTireCategoryMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, logError} from '@dms/shared';

import {ApiError, Nullish, useNavigate} from 'shared/web';

import {TireCategoryForm, TireCategoryFormSchema} from '../types/TireCategoryForm';

interface TireCategoryDetailFormProps {
  tireCategory: GetTireCategoryApiResponse | Nullish;
}

export function TireCategoryDetailForm(props: TireCategoryDetailFormProps) {
  const navigate = useNavigate();

  const isDraft = isNil(props.tireCategory);

  const {branchesOptions, isLoadingBranches} = useGetBranchListQuery(undefined, {
    selectFromResult: (result) => ({
      branchesOptions:
        result.data?.branchListItems?.map((branch) => ({
          label: branch.marketingName,
          value: branch.id,
        })) ?? [],
      isLoadingBranches: result.isLoading,
    }),
  });

  const [postTireCategory] = usePostTireCategoryMutation();
  const [patchTireCategory] = usePatchTireCategoryMutation();

  const defaultValues: Partial<TireCategoryForm> = {
    name: props.tireCategory?.name,
    branchIds: props.tireCategory?.branchIds,
  };

  const handleSubmit: FormSubmitHandler<TireCategoryForm> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isDraft) {
      return await postTireCategory({
        body: {
          name: formValues.name,
          branchIds: formValues.branchIds,
        },
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.successfullyCreated'));
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.tireCategory);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    if (isNil(props.tireCategory?.tireCategoryId)) {
      return logError('tireCategoryId is not defined');
    }

    await patchTireCategory({
      tireCategoryId: props.tireCategory.tireCategoryId,
      body: {
        name: formValues.name,
        branchIds: formValues.branchIds,
      },
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.tireCategory);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <Form<TireCategoryForm>
      experimentalZodSchema={TireCategoryFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, _formApi, navigateWithoutBlocker) => (
        <>
          <Grid columns={3}>
            <FormField
              isRequired
              control={control}
              name="name"
              type="text"
              label={i18n.t('general.labels.name')}
            />
            <FormField
              isLoading={isLoadingBranches}
              control={control}
              name="branchIds"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.branch')}
              options={branchesOptions}
              helperText={i18n.t('entity.settings.labels.branchesHelperText')}
            />
          </Grid>
          <SettingsFooter
            actions={[
              {
                type: 'button',
                title: isDraft
                  ? i18n.t('general.actions.discard')
                  : i18n.t('general.actions.discardChanges'),
                variant: 'secondary',
                onClick: () => navigateWithoutBlocker(settingsRoutes.tireCategory),
              },
              {
                control,
                type: 'form-button',
                buttonType: 'submit',
                title: isDraft
                  ? i18n.t('general.actions.create')
                  : i18n.t('general.actions.saveChanges'),
              },
            ]}
          />
        </>
      )}
    </Form>
  );
}
